import { Link } from "react-router-dom";
import { T } from "@/components/T";
import { useLang } from "@/context/LanguageContext";
import { UPCOMING } from "@/lib/upcoming";
import Seo from "@/components/Seo";
import { Divider } from "@/components/MotifBackground";
import { ExternalLink } from "lucide-react";

const GROUPS = [
  {
    deva: "ज्ञान", en: "Knowledge",
    links: [
      { to: "/kathas", deva: "कथा", en: "Kathas" },
      { to: "/mantras", deva: "मंत्र", en: "Mantras" },
      { to: "/suktas", deva: "सूक्त", en: "Suktas & Vedic Knowledge" },
      { to: "/stotras", deva: "स्तोत्र", en: "Stotras" },
      { to: "/rituals", deva: "पूजा विधि", en: "Rituals & Pooja Guides" },
      { to: "/festivals", deva: "पर्व", en: "Festivals & Observances" },
      { to: "/glossary", deva: "शब्दकोश", en: "Glossary" },
    ],
  },
  {
    deva: "परंपरा", en: "Tradition & Hub",
    links: [
      { to: "/scriptures", deva: "शास्त्र", en: "Scriptures" },
      { to: "/rishis", deva: "ऋषि", en: "Rishis" },
      { to: "/philosophy", deva: "दर्शन", en: "Philosophy" },
      { to: "/pilgrimage", deva: "तीर्थ", en: "Pilgrimage" },
      { to: "/samskaras", deva: "संस्कार", en: "Samskaras" },
      { to: "/sanskrit", deva: "संस्कृत", en: "Sanskrit" },
    ],
  },
  {
    deva: "अन्वेषण", en: "Explore",
    links: [
      { to: "/deities", deva: "देवता", en: "Deities" },
      { to: "/temples", deva: "मंदिर", en: "Temples" },
      { to: "/panchang", deva: "पंचांग", en: "Panchangam" },
      { to: "/calendar", deva: "पंचांग कैलेंडर", en: "Festival Calendar" },
      { to: "/quiz", deva: "प्रश्नोत्तरी", en: "Quiz" },
      { to: "/patrika", deva: "पत्रिका", en: "Patrika (Blog)" },
      { to: "/live-darshan", deva: "लाइव दर्शन", en: "Live Darshan" },
    ],
  },
  {
    deva: "सेवाएँ एवं समुदाय", en: "Services & Community",
    links: [
      { to: "/services", deva: "सेवाएँ", en: "Services" },
      { to: "/services/numerology", deva: "अंक ज्योतिष", en: "Numerology" },
      { to: "/japa", deva: "जप", en: "Japa Counter" },
      { to: "/leaderboard", deva: "अग्रणी", en: "Leaderboard" },
      { to: "/invite", deva: "आमंत्रण", en: "Invite" },
    ],
  },
  {
    deva: "संस्था", en: "Institution",
    links: [
      { to: "/", deva: "मुख्य पृष्ठ", en: "Home" },
      { to: "/explore", deva: "अन्वेषण", en: "Explore All" },
      { to: "/about", deva: "परिचय", en: "About" },
      { to: "/feedback", deva: "संपर्क एवं प्रतिक्रिया", en: "Contact & Feedback" },
      { to: "/support", deva: "सहयोग", en: "Support" },
      { to: "/ganesh-chaturthi", deva: "गणेश चतुर्थी", en: "Ganesh Chaturthi" },
    ],
  },
  {
    deva: "आगामी", en: "Coming Soon",
    links: UPCOMING.map((u) => ({ to: u.path, deva: u.hi, en: u.en })),
  },
];

export default function SitemapPage() {
  const { pick } = useLang();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-20" data-testid="sitemap-page">
      <Seo
        path="/sitemap"
        title="Sitemap — All Pages"
        description="A complete map of Dharmartha — every section, page and resource on the site, linked for easy discovery."
        keywords="Dharmartha sitemap, all pages, site map, index"
      />
      <p className="overline-label mb-3"><T>Site Index</T></p>
      <h1 className="font-serif-latin text-4xl text-ink md:text-5xl">{pick("साइट मानचित्र", "Sitemap")}</h1>
      <p className="mt-4 max-w-2xl leading-relaxed text-ink-2">
        {pick(
          "धर्मार्थ के सभी अनुभाग एवं पृष्ठ — सुगम खोज एवं अन्वेषण हेतु एक ही स्थान पर।",
          "Every section and page on Dharmartha, gathered in one place for easy discovery."
        )}
      </p>

      <Divider className="my-10" />

      <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {GROUPS.map((g) => (
          <div key={g.en} data-testid={`sitemap-group-${g.en.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h2 className="mb-4 font-deva text-xl text-ink deva-lh">{pick(g.deva, g.en)} <span className="ml-1 font-serif-latin text-sm text-ink-3">· {g.en}</span></h2>
            <ul className="space-y-2.5">
              {g.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline text-sm text-ink-2 transition-colors hover:text-saffron">
                    {pick(l.deva, l.en)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <Divider className="my-12" />
      <a href={`${BACKEND}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer" data-testid="sitemap-xml-link"
        className="inline-flex items-center gap-2 text-sm text-saffron hover:underline">
        {pick("XML साइटमैप (खोज इंजनों हेतु)", "XML Sitemap (for search engines)")} <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
}
